import { cn } from "@/lib/utils"

interface FooterActionBarProps {
  /** Left-aligned meta content (status text, counts, lock notice…). */
  left?: React.ReactNode
  /** Right-aligned action buttons. */
  children: React.ReactNode
  /** Escape hatch for padding/spacing overrides on full-width page footers. */
  className?: string
}

/**
 * Unified footer for modules and modals: dark bar, top border, left meta on the
 * left and action buttons pushed to the right. Use this for every footer so the
 * UI stays in sync across screens.
 *
 * Narrow containers (a resized detail panel) drop the buttons onto their own row
 * rather than squeezing the meta text into a sliver, and the buttons wrap among
 * themselves if even that row is too tight.
 */
export function FooterActionBar({
  left,
  children,
  className,
}: FooterActionBarProps) {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-grey4 bg-grey1 px-6 py-4",
        className,
      )}
    >
      {/* A real basis (not grow-from-0) is what makes the row break here instead of
          collapsing this text first — see DetailPanelHeader for the same reasoning.
          Omitted entirely when there's no meta, so that basis can't reserve width a
          footer of buttons alone doesn't need. */}
      {left != null && (
        <div className="flex min-w-0 grow basis-64 items-center gap-2 text-[13px] text-grey5">
          {left}
        </div>
      )}
      {/* Shrinkable so it can wrap internally; the buttons themselves keep their size. */}
      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-3 *:shrink-0">
        {children}
      </div>
    </div>
  )
}
